import React from "react";
import { createStyles } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { Body, Label } from "../../../../components/Typography";
import { BreakdownTable } from "./BreakdownTable";
import { OrderDetailCard, CardSection } from "./OrderDetailCard";
import { PaymentLink } from "./PaymentLink";
import { paymentStatusName } from "../../../../utils/paymentStatusNames";
import { formatOrderDate, hasCapturedPayment, isRefundOutcome, type Order } from "./shared";

const useStyles = createStyles(() => ({
  paymentMethod: {
    color: tokens.color.primitive.light.gray[700],
  },
  refundNote: {
    color: tokens.color.semantic.text.subtle,
  },
}));

const SANDBOX_PAYMENT_MESSAGE = "There are no payments for test orders in the sandbox.";

const PROCESSING_REFUND_MESSAGE = "The refund is in progress and can take a few days to settle.";

export const PaymentCard: React.FC<{ order: Order; isSandbox: boolean }> = ({ order, isSandbox }) => {
  const { classes } = useStyles();
  const statusName = order.paymentStatus ? paymentStatusName(order.paymentStatus) : "";
  const captured = hasCapturedPayment(order.paymentStatus);
  const refundOutcome = isRefundOutcome(order.paymentStatus);

  if (isSandbox) {
    return (
      <OrderDetailCard>
        <CardSection>
          <Label>Payment Status</Label>
          <Body>{SANDBOX_PAYMENT_MESSAGE}</Body>
        </CardSection>
      </OrderDetailCard>
    );
  }

  return (
    <OrderDetailCard>
      <CardSection>
        <Label>Payment Status</Label>
        <Body data-testid="payment-status-line">
          {statusName}
          {captured && order.salePayment?.createdAt && (
            <>
              {refundOutcome ? " — paid on " : " on "}
              {formatOrderDate(order.salePayment.createdAt)}
            </>
          )}
          {captured && typeof order.salePayment?.id === "number" && <PaymentLink paymentId={order.salePayment.id} />}
        </Body>
        {captured && order.salePayment?.paymentMethodLabel && (
          <Body className={classes.paymentMethod}>via {order.salePayment.paymentMethodLabel}</Body>
        )}
        {order.paymentStatus === GraphQL.PaymentStatus.ProcessingRefund && (
          <Body className={classes.refundNote} data-testid="processing-refund-note">
            {PROCESSING_REFUND_MESSAGE}
          </Body>
        )}
      </CardSection>

      {order.pricing && (
        <BreakdownTable
          data={{
            currency: order.pricing.currency,
            itemsTotal: order.pricing.itemsTotal,
            shippingTotal: order.pricing.shippingTotal,
            taxesTotal: order.pricing.taxesTotal,
            total: order.pricing.orderTotal,
          }}
          sectionLabel="Total Order Amount"
          totalLabel="Order Total"
        />
      )}
    </OrderDetailCard>
  );
};
